import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Tag, Plus, X, Edit3, Check, AlertCircle, ChevronDown } from 'lucide-react';
import api from '../../../../api/client';
import { getTagColor } from '../../../../utils/tagColors';
import TagEmailConfirmModal from './TagEmailConfirmModal';
import EmailPreviewModal from './EmailPreviewModal';

const TagEditor = ({ 
    tags = [], 
    sessionId, 
    onTagsUpdate, 
    isCompanyAdmin = false,
    maxTags = 5,
    candidateName = null // Optional candidate name for email modals
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editingTags, setEditingTags] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [newTagInput, setNewTagInput] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [newTagCategory, setNewTagCategory] = useState('interview');
    const [creatingTag, setCreatingTag] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredTags, setFilteredTags] = useState([]);
    const [fetchingTags, setFetchingTags] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    
    // Email modal states
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showEmailPreview, setShowEmailPreview] = useState(false);
    const [emailPreviewData, setEmailPreviewData] = useState(null);
    const [pendingTags, setPendingTags] = useState([]);
    const [triggerTagType, setTriggerTagType] = useState(null);
    const [sendingEmail, setSendingEmail] = useState(false);
    
    // Refs
    const hasFetchedTags = useRef(false);
    const inputRef = useRef(null);

    // Initialize editing tags when entering edit mode
    useEffect(() => {
        if (isEditing) {
            setEditingTags([...tags]);
        }
    }, [isEditing, tags]);


    // Filter tags based on input
    useEffect(() => {
        if (newTagInput) {
            const filtered = availableTags
                .filter(tag => {
                    const tagName = typeof tag === 'string' ? tag : tag.name;
                    return tagName.toLowerCase().includes(newTagInput.toLowerCase()) && 
                           !editingTags.includes(tagName);
                })
                .slice(0, 10);
            setFilteredTags(filtered);
            setShowDropdown(true);
        } else {
            setFilteredTags(availableTags.filter(tag => {
                const tagName = typeof tag === 'string' ? tag : tag.name;
                return !editingTags.includes(tagName);
            }).slice(0, 10));
        }
    }, [newTagInput, availableTags, editingTags]);

    // Fetch available tags - only called when explicitly needed
    const fetchAvailableTags = async () => {
        if (hasFetchedTags.current || fetchingTags) {
            return; // Prevent duplicate calls
        }
        
        try {
            setFetchingTags(true);
            hasFetchedTags.current = true;
            
            const response = await api.get('tags/', {
                headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` }
            });
            
            const tagsList = response.data.results || response.data || [];
            console.log('Fetched available tags:', tagsList);
            setAvailableTags(tagsList);
            
        } catch (err) {
            console.error('Error fetching available tags:', err);
            setError('Failed to load available tags');
            // Reset flag on error so it can retry
            hasFetchedTags.current = false;
        } finally {
            setFetchingTags(false);
        }
    };

    const handleEditStart = async () => {
        setIsEditing(true);
        setError('');
        setNewTagInput('');
        
        // Only fetch tags if we haven't already and we're a company admin
        if (isCompanyAdmin && !hasFetchedTags.current && availableTags.length === 0) {
            await fetchAvailableTags();
        }
    };

    const handleEditCancel = useCallback(() => {
        setIsEditing(false);
        setEditingTags([...tags]);
        setError('');
        setNewTagInput('');
        setShowCreateModal(false);
        setNewTagName('');
        setNewTagCategory('interview');
        setShowDropdown(false);
        // Reset email modal states to prevent any lingering modals
        setShowConfirmModal(false);
        setShowEmailPreview(false);
        setPendingTags([]);
        setTriggerTagType(null);
        setEmailPreviewData(null);
    }, [tags]);

    const handleTagRemove = useCallback((tagToRemove) => {
        setEditingTags(prev => prev.filter(tag => tag !== tagToRemove));
    }, []);

    const handleAddTag = useCallback((tagName) => {
        const trimmedTag = tagName.trim();
        if (!trimmedTag) return;
        
        if (editingTags.includes(trimmedTag)) {
            setError('Tag already added');
            return;
        }
        
        if (editingTags.length >= maxTags) {
            setError(`Maximum ${maxTags} tags allowed`);
            return;
        }

        setEditingTags(prev => [...prev, trimmedTag]);
        setNewTagInput('');
        setShowDropdown(false);
        setError('');
    }, [editingTags, maxTags]);

    const handleInputChange = useCallback((e) => {
        setNewTagInput(e.target.value);
        setError('');
    }, []);

    // Update dropdown position when input is focused or dropdown is shown
    const updateDropdownPosition = useCallback(() => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 4, // 4px margin (mt-1)
                left: rect.left,
                width: rect.width
            });
        }
    }, []);

    useEffect(() => {
        if (showDropdown && inputRef.current) {
            updateDropdownPosition();
            // Update position on scroll/resize
            const handleUpdate = () => updateDropdownPosition();
            window.addEventListener('scroll', handleUpdate, true);
            window.addEventListener('resize', handleUpdate);
            
            // Close dropdown when clicking outside
            const handleClickOutside = (e) => {
                const dropdown = document.querySelector('[data-tag-dropdown]');
                const clickedInput = inputRef.current && inputRef.current.contains(e.target);
                const clickedDropdown = dropdown && dropdown.contains(e.target);
                
                if (!clickedInput && !clickedDropdown) {
                    setShowDropdown(false);
                }
            };
            
            // Use setTimeout to avoid immediate closure on focus
            setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 0);
            
            return () => {
                window.removeEventListener('scroll', handleUpdate, true);
                window.removeEventListener('resize', handleUpdate);
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showDropdown, updateDropdownPosition]);

    // Check if tags contain trigger tags
    // const getTriggerTag = (tags) => {
    //     const triggerTags = ['Rejected', 'Selected', '1-to-1 with manager', '1-to-1 discussion with manager'];
    //     for (const tag of tags) {
    //         const normalized = tag.toLowerCase().trim();
    //         for (const triggerTag of triggerTags) {
    //             if (normalized === triggerTag.toLowerCase()) {
    //                 // Standardize manager tag to backend format
    //                 if (normalized === '1-to-1 discussion with manager') {
    //                     return '1-to-1 with manager';
    //                 }
    //                 return triggerTag;
    //             }
    //         }
    //     }
    //     return null;
    // };

    // Detect if trigger tag was added
    const detectTriggerTagChange = (oldTags, newTags) => {
        const oldSet = new Set((oldTags || []).map(t => t.toLowerCase().trim()));
        
        // Check each new tag to see if it was added and is a trigger tag
        for (const tag of newTags || []) {
            const normalized = tag.toLowerCase().trim();
            // Check if this tag is new (wasn't in old tags)
            if (!oldSet.has(normalized)) {
                // Check if it's a trigger tag (case-insensitive)
                const triggerTags = ['rejected', 'selected', '1-to-1 with manager', '1-to-1 discussion with manager'];
                for (const triggerTag of triggerTags) {
                    if (normalized === triggerTag.toLowerCase()) {
                        if (normalized === '1-to-1 discussion with manager') {
                            return '1-to-1 with manager'; // Standardize to backend format
                        }
                        return triggerTag; // Return canonical trigger tag
                    }
                }
            }
        }
        return null;
    };

    const handleSaveTags = async () => {
        try {
            setError('');
            // Check if any trigger tags were added
            const addedTriggerTag = detectTriggerTagChange(tags, editingTags);
            
            if (addedTriggerTag && isCompanyAdmin) {
                // Show confirmation modal first
                setPendingTags([...editingTags]);
                setTriggerTagType(addedTriggerTag);
                setShowConfirmModal(true);
                return;
            }
            
            // No trigger tags, save normally
            await saveTagsDirectly(editingTags);
            
        } catch (err) {
            console.error('Error in handleSaveTags:', err);
            alert('Error: ' + err.message);
            setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to process tags.');
        }
    };

    const saveTagsDirectly = async (tagsToSave) => {
        try {
            setLoading(true);
            
            await api.patch(`sessions/${sessionId}/`, {
                tags: tagsToSave
            }, {
                headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` }
            });
            
            if (onTagsUpdate) {
                onTagsUpdate(tagsToSave);
            }
            
            setIsEditing(false);
            setNewTagInput('');
            setShowDropdown(false);
            
        } catch (err) {
            console.error('Error updating tags:', err);
            setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to update tags. Please try again.');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmEmail = async () => {
        setShowConfirmModal(false);
        
        try {
            setLoading(true);
            setError('');
            
            // Call preview API
            const previewResponse = await api.post(`sessions/${sessionId}/preview_tag_email/`, {
                tag_type: triggerTagType
            }, {
                headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` }
            });
            
            setEmailPreviewData(previewResponse.data);
            setShowEmailPreview(true);
            
        } catch (err) {
            console.error('Error generating email preview:', err);
            setError(err.response?.data?.error || 'Failed to generate email preview. Please try again.');
            setShowConfirmModal(false);
        } finally {
            setLoading(false);
        }
    };

    const handleSendEmail = async (emailPayload) => {
        try {
            setSendingEmail(true);
            setError('');
            
            // Call send email API
            const sendResponse = await api.post(`sessions/${sessionId}/send_tag_email/`, {
                tags: pendingTags,
                tag_type: triggerTagType,
                email_data: emailPayload
            }, {
                headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` }
            });
            
            // Save tags after successful email send
            if (pendingTags.length > 0) {
                await saveTagsDirectly(pendingTags);
            }
            
            setShowEmailPreview(false);
            setPendingTags([]);
            setTriggerTagType(null);
            setEmailPreviewData(null);
            
            // Show success message (you can add a toast notification here)
            console.log('Email sent successfully:', sendResponse.data);
            
        } catch (err) {
            console.error('Error sending email:', err);
            setError(err.response?.data?.error || 'Failed to send email. Please try again.');
        } finally {
            setSendingEmail(false);
        }
    };

    const handleCancelEmail = async () => {
        // User clicked "No" - save tags without sending email
        const tagsToSave = pendingTags.length > 0 ? pendingTags : editingTags;
        setShowConfirmModal(false);
        setShowEmailPreview(false);
        setPendingTags([]);
        setTriggerTagType(null);
        setEmailPreviewData(null);
        
        // Save tags without sending email
        if (tagsToSave.length > 0 || editingTags.length > 0) {
            try {
                await saveTagsDirectly(tagsToSave.length > 0 ? tagsToSave : editingTags);
            } catch (err) {
                console.error('Error saving tags after canceling email:', err);
            }
        }
    };

    const handleCreateNewTag = async () => {
        if (!newTagName.trim()) return;
        
        try {
            setCreatingTag(true);
            setError('');
            
            const response = await api.post('tags/', {
                name: newTagName.trim(),
                category: newTagCategory
            }, {
                headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` }
            });
            
            const newTag = response.data.name;
            setAvailableTags(prev => [...prev, response.data]);
            setEditingTags(prev => [...prev, newTag]);
            
            setNewTagName('');
            setNewTagCategory('interview');
            setShowCreateModal(false);
            
        } catch (err) {
            console.error('Error creating tag:', err);
            setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to create new tag. Please try again.');
        } finally {
            setCreatingTag(false);
        }
    };

    // Create Tag Modal Component
    const CreateTagModal = () => {
        if (!showCreateModal) return null;

        const modalContent = (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={() => {
                setShowCreateModal(false);
                setNewTagName('');
                setNewTagCategory('interview');
                setError('');
            }}>
                <div className="bg-white rounded-lg shadow-xl w-full max-w-sm relative" style={{ zIndex: 10000 }} onClick={(e) => e.stopPropagation()}>
                    <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-1.5">
                                <Plus size={16} className="text-blue-600" />
                                <h3 className="text-sm font-semibold text-gray-900">Create New Tag</h3>
                            </div>
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setNewTagName('');
                                    setNewTagCategory('interview');
                                    setError('');
                                }}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                            >
                                <X size={16} className="text-gray-500" />
                            </button>
                        </div>
                        
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Tag Name *
                                </label>
                                <input
                                    type="text"
                                    value={newTagName}
                                    onChange={(e) => setNewTagName(e.target.value)}
                                    placeholder="e.g., Technical Round"
                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    autoFocus
                                />
                            </div>
                            
                            <div className="relative">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Category
                                </label>
                                <select
                                    value={newTagCategory}
                                    onChange={(e) => setNewTagCategory(e.target.value)}
                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none bg-white"
                                    style={{ zIndex: 10001 }}
                                >
                                    <option value="interview">Interview</option>
                                    <option value="candidate">Candidate</option>
                                    <option value="job">Job</option>
                                </select>
                            </div>

                            {error && (
                                <div className="flex items-center gap-1.5 p-2 bg-red-50 border border-red-200 rounded-md">
                                    <AlertCircle size={12} className="text-red-600" />
                                    <span className="text-xs text-red-700">{error}</span>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setNewTagName('');
                                    setNewTagCategory('interview');
                                    setError('');
                                }}
                                disabled={creatingTag}
                                className="flex-1 px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateNewTag}
                                disabled={!newTagName.trim() || creatingTag}
                                className="flex-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                            >
                                {creatingTag ? (
                                    <>
                                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Plus size={12} />
                                        Create
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );

        // Render modal using portal at document root to ensure it's on top
        return typeof document !== 'undefined' 
            ? createPortal(modalContent, document.body)
            : modalContent;
    };

    // Handle empty tags case
    if (!isCompanyAdmin && (!tags || tags.length === 0)) {
        return (
            <div className="flex items-center gap-1 text-gray-400 text-[10px]">
                <Tag size={10} />
                <span>No tags assigned</span>
            </div>
        );
    }

    // Email modals - always render via portal
    const renderEmailModals = () => {
        if (typeof document === 'undefined') return null;
        return createPortal(
            <>
                <TagEmailConfirmModal
                    isOpen={showConfirmModal}
                    onClose={handleCancelEmail}
                    onConfirm={handleConfirmEmail}
                    tagType={triggerTagType}
                    candidateName={candidateName}
                />
                <EmailPreviewModal
                    isOpen={showEmailPreview}
                    onClose={handleCancelEmail}
                    onSend={handleSendEmail}
                    emailData={emailPreviewData}
                    loading={sendingEmail}
                    candidateName={candidateName}
                />
            </>,
            document.body
        );
    };

    // Editing mode
    if (isEditing) {
        return (
            <>
                {renderEmailModals()}
                
                <div className="flex flex-col gap-1.5 relative z-50 bg-white p-2 rounded-md border border-gray-200 shadow-lg">
                    {/* Current tags with remove buttons */}
                    <div className="flex flex-wrap gap-1 items-center">
                        {editingTags.map((tag, index) => {
                            const colors = getTagColor(tag);
                            return (
                                <span
                                    key={index}
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${colors.bg} ${colors.text} border ${colors.border} transition-all`}
                                    title={`Tag: ${tag}`}
                                >
                                    <Tag size={8} />
                                    <span>{tag}</span>
                                    <button
                                        onClick={() => handleTagRemove(tag)}
                                        className="ml-0.5 p-0.5 hover:bg-black hover:bg-opacity-10 rounded transition-colors"
                                        title="Remove tag"
                                    >
                                        <X size={8} />
                                    </button>
                                </span>
                            );
                        })}
                    </div>

                    {/* Add new tag input with dropdown */}
                    {editingTags.length < maxTags && (
                        <div className="relative z-[60]">
                            <div className="flex gap-1">
                                <div className="relative flex-1">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={newTagInput}
                                        onChange={handleInputChange}
                                        onFocus={() => {
                                            updateDropdownPosition();
                                            setShowDropdown(true);
                                        }}
                                        placeholder="Add tag..."
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                        disabled={fetchingTags}
                                    />
                                </div>
                                <button
                                    onClick={() => {
                                        updateDropdownPosition();
                                        setShowDropdown(!showDropdown);
                                    }}
                                    className="px-1.5 py-1 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
                                    title="Show all tags"
                                    disabled={fetchingTags}
                                >
                                    <ChevronDown size={12} />
                                </button>
                            </div>
                            
                            {/* Dropdown for available tags - rendered via portal */}
                            {showDropdown && !fetchingTags && typeof document !== 'undefined' && (
                                createPortal(
                                    <div 
                                        data-tag-dropdown
                                        className="fixed bg-white border border-gray-300 rounded-md shadow-xl z-[10002] max-h-40 overflow-y-auto"
                                        style={{
                                            top: `${dropdownPosition.top}px`,
                                            left: `${dropdownPosition.left}px`,
                                            width: `${dropdownPosition.width}px`,
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {(filteredTags.length === 0 && !newTagInput) ? (
                                            <div className="px-2 py-1.5 text-xs text-gray-500 text-center italic">
                                                No tags found
                                            </div>
                                        ) : (
                                            <>
                                                {filteredTags.map((availableTag, index) => {
                                                    const tagName = typeof availableTag === 'string' ? availableTag : availableTag.name;
                                                    return (
                                                        <button
                                                            key={index}
                                                            onClick={() => handleAddTag(tagName)}
                                                            className="w-full px-2 py-1.5 text-xs text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                                        >
                                                            {tagName}
                                                        </button>
                                                    );
                                                })}
                                                {newTagInput && !filteredTags.some(t => {
                                                    const tagName = typeof t === 'string' ? t : t.name;
                                                    return tagName.toLowerCase() === newTagInput.toLowerCase().trim();
                                                }) && (
                                                    <button
                                                        onClick={() => {
                                                            setShowCreateModal(true);
                                                            setNewTagName(newTagInput);
                                                        }}
                                                        className="w-full px-2 py-1.5 text-xs text-left hover:bg-blue-50 border-t border-gray-200 flex items-center gap-1 text-blue-600"
                                                    >
                                                        <Plus size={12} />
                                                        Create "{newTagInput}"
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>,
                                    document.body
                                )
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center gap-1 text-[10px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                            <AlertCircle size={10} />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex items-center gap-1.5 pt-0.5">
                        <button
                            type="button"
                            onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                
                                if (loading || !isEditing) {
                                    return;
                                }
                                
                                try {
                                    await handleSaveTags();
                                } catch (error) {
                                    console.error('Error saving tags:', error);
                                    setError('Failed to save tags: ' + error.message);
                                }
                            }}
                            disabled={loading}
                            className="flex items-center gap-1 px-2 py-0.5 bg-blue-600 text-white text-[10px] rounded-md hover:bg-blue-700 disabled:opacity-50 transition-all"
                            title="Save changes"
                        >
                            {loading ? (
                                <div className="w-2.5 h-2.5 border border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Check size={10} />
                            )}
                            <span>Save</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleEditCancel}
                            className="flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-700 text-[10px] rounded-md hover:bg-gray-300 transition-colors"
                            title="Cancel"
                        >
                            <X size={10} />
                            <span>Cancel</span>
                        </button>
                        {fetchingTags && (
                            <div className="w-2.5 h-2.5 border border-gray-400 border-t-transparent rounded-full animate-spin ml-1" />
                        )}
                    </div>
                </div>

                {/* Create Tag Modal */}
                <CreateTagModal />
            </>
        );
    }

    // Display mode (non-editing)
    return (
        <>
            {/* Display tags */}
            {tags && tags.length > 0 ? (
                <div className="flex flex-wrap gap-1 items-center">
                    {tags.slice(0, maxTags).map((tag, index) => {
                        const colors = getTagColor(tag);
                        return (
                            <span
                                key={index}
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${colors.bg} ${colors.text} border ${colors.border} transition-all`}
                                title={`Tag: ${tag}`}
                            >
                                <Tag size={8} />
                                {tag}
                            </span>
                        );
                    })}
                    
                    {tags.length > maxTags && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 text-[10px]">
                            +{tags.length - maxTags}
                        </span>
                    )}
                    
                    {isCompanyAdmin && (
                        <button
                            onClick={handleEditStart}
                            className="p-0.5 text-blue-600 hover:bg-blue-100 rounded transition-colors ml-0.5"
                            title="Edit tags"
                        >
                            <Edit3 size={10} />
                        </button>
                    )}
                </div>
            ) : (
                <div className="flex items-center gap-1">
                    <span className="text-gray-400 text-[10px]">
                        {isCompanyAdmin ? 'No tags' : 'No tags assigned'}
                    </span>
                    {isCompanyAdmin && (
                        <button
                            onClick={handleEditStart}
                            className="p-0.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                            title="Edit tags"
                        >
                            <Edit3 size={10} />
                        </button>
                    )}
                </div>
            )}

        </>
    );
};

export default React.memo(TagEditor);